import React, { useContext } from 'react';
import styled, { useTheme } from 'styled-components';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import Card from '@organisms/cards/Card';
import Icon from '@components/Icon';
import ThumbnailWithRatio from '@components/ThumbnailWithRatio';
import {
  ProgramCategory,
  ProgramTextElementFieldsFragment,
} from '@gql/generated';
import { ProgramCategoryNames, programTypes } from '@lib/constants';

export const StyledCard = styled(Card)<{ category: ProgramCategory }>`
  padding-left: 0;
  margin-bottom: 20px;

  ${ThumbnailWithRatio.toString()} {
    height: 100%;
    padding-top: 30%;
    background-color: ${({ theme, category }) =>
      theme.helpers.getProgramThemeColor(category)}};

    img {
      top: 5%;
      height: 90%;
      object-fit: contain;
      object-position: -2%;
    }
  }
`;

export const ThumbnailWrapper = styled.div<{
  locked?: boolean;
  category: ProgramCategory;
}>`
  align-items: center;
  justify-content: center;
  display: flex;
  width: 100%;
  height: 100%;

  &:hover {
    cursor: pointer;

    & > .changeBackgroundOnHover {
      box-shadow: 0px 0px 14px 0px rgba(0, 0, 0, 0.75);
    }
  }
`;

export const TagsWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: start;
  height: 100%;
  width: 100%;
  padding: 10px;
`;

export const VideoTag = styled.span`
  color: ${props => props.theme.colors.neutrals.white};
  border-radius: 3px;
  padding: 5px;
  margin-right: 8px;
  background-color: ${props => props.theme.colors.primary.stone};
`;

type Props = {
  textElement: ProgramTextElementFieldsFragment;
  redirectTo: string;
  onClick?(): void;
  locked?: boolean;
  tags?: string[];
  category: ProgramCategory;
};

const ProgramTextCard = ({
  textElement,
  redirectTo,
  locked,
  category,
  tags = [],
  onClick = null,
}: Props) => {
  const theme = useTheme();

  let isMobile = false;
  if (typeof window !== 'undefined') {
    isMobile = window.matchMedia(`(max-width: ${theme.viewports.laptop}px)`)
      .matches;
  }

  const programType = t({ id: ProgramCategoryNames[category] });

  return (
    <StyledCard
      data-testid="ProgramTextCard"
      large={!isMobile}
      onClick={onClick}
      redirectTo={redirectTo}
      thumbnailUrl="/static/icons/document-icon.svg"
      contentLeftCardLargeSection={[]}
      category={category}
      thumbnailContent={
        <ThumbnailWrapper locked={locked !== false} category={category}>
          {locked !== false ? (
            <Icon
              name="ygb-icon-programlock-ic"
              color="white"
              fontSize="30px"
              style={{ zIndex: 1 }}
            />
          ) : null}
          {tags && (
            <TagsWrapper>
              {tags?.map((tag, i) => {
                let content = null;
                if (typeof tag === 'string') {
                  content = <Trans id={tag} />;
                } else {
                  content = tag;
                }
                return <VideoTag key={i}>{content}</VideoTag>;
              })}
            </TagsWrapper>
          )}
        </ThumbnailWrapper>
      }
      contentTitle={textElement.title}
      contentDescription={
        locked
          ? t({
              id: `programs_2021.overview.text_card.locked_text`,
              values: {
                programType: programType,
              },
            })
          : textElement.description
      }
    />
  );
};

export default ProgramTextCard;
